/**
 * Timeline API
 *
 * RTK Query endpoints for the user success timeline.
 */

import { api } from '../api';

// ====================
// Types
// ====================

export type TimelineEventType =
  | 'npc_level_passed'
  | 'practice_completed'
  | 'authority_rank_up'
  | 'coaching_session_completed'
  | 'certification_earned'
  | 'badge_earned'
  | 'training_completed'
  | 'portfolio_published'
  | 'coach_client_milestone'
  | 'org_milestone'
  | 'credential_issued'
  | 'application_submitted'
  | 'candidate_hired'
  | 'candidate_rejected';

export type TimelineVisibility = 'private' | 'public';

export interface TimelineEventMetadata {
  // NPC related
  npcPersonaId?: string;
  npcPersonaLabel?: string;
  npcLevelLabel?: string;
  npcTier?: string;
  xpAwarded?: number;

  // Scores
  scoreOverall?: number;
  scoreClarity?: number;
  scorePace?: number;
  scoreConfidence?: number;

  // Authority related
  authorityBefore?: number;
  authorityAfter?: number;
  authorityTierBefore?: string;
  authorityTierAfter?: string;

  // Coaching related
  coachUserId?: string;
  coachName?: string;
  engagementId?: string;

  // Organization related
  orgId?: string;
  orgName?: string;
  moduleId?: string;
  moduleName?: string;

  // Domain/Scenario related
  domainKey?: string;
  scenarioKey?: string;

  // Badge/Certification related
  badgeKey?: string;
  certificationName?: string;

  // Portfolio related
  highlightTitle?: string;
  highlightId?: string;

  // Session related
  sessionDurationSeconds?: number;
  sessionType?: string;

  // Hiring/Verification related
  verificationStatus?: string;
  pipelineId?: string;
  pipelineTitle?: string;
}

export interface UserTimelineEvent {
  id: string;
  userId: string;
  type: TimelineEventType;
  title: string;
  description?: string;
  iconKey: string;
  occurredAt: string;
  metadata?: TimelineEventMetadata;
  visibility: TimelineVisibility;
}

export interface TimelineResponse {
  events: UserTimelineEvent[];
  pagination: {
    limit: number;
    offset: number;
    count: number;
  };
}

export interface TimelineStats {
  totalEvents: number;
  byType: Record<TimelineEventType, number>;
  publicEvents: number;
  recentActivity: number;
}

export interface GetTimelineParams {
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
  types?: TimelineEventType[];
}

// ====================
// Icon Mappings
// ====================

export const TIMELINE_ICONS: Record<string, string> = {
  trophy: '🏆',
  microphone: '🎤',
  star: '⭐',
  people: '👥',
  ribbon: '🎖️',
  medal: '🏅',
  school: '🎓',
  portfolio: '📁',
  handshake: '🤝',
  building: '🏢',
  shield: '🛡️',
  briefcase: '💼',
  'check-circle': '✅',
  'x-circle': '❌',
};

export const TIMELINE_EVENT_LABELS: Record<TimelineEventType, string> = {
  npc_level_passed: 'NPC Progress',
  practice_completed: 'Practice',
  authority_rank_up: 'Authority',
  coaching_session_completed: 'Coaching',
  certification_earned: 'Certification',
  badge_earned: 'Badge',
  training_completed: 'Training',
  portfolio_published: 'Portfolio',
  coach_client_milestone: 'Milestone',
  org_milestone: 'Organization',
  credential_issued: 'Credential',
  application_submitted: 'Applied',
  candidate_hired: 'Hired',
  candidate_rejected: 'Application Closed',
};

export const TIMELINE_EVENT_COLORS: Record<TimelineEventType, string> = {
  npc_level_passed: 'amber',
  practice_completed: 'blue',
  authority_rank_up: 'purple',
  coaching_session_completed: 'green',
  certification_earned: 'indigo',
  badge_earned: 'pink',
  training_completed: 'teal',
  portfolio_published: 'cyan',
  coach_client_milestone: 'emerald',
  org_milestone: 'slate',
  credential_issued: 'indigo',
  application_submitted: 'blue',
  candidate_hired: 'green',
  candidate_rejected: 'slate',
};

// ====================
// API Endpoints
// ====================

export const timelineApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get timeline for authenticated user
    getMyTimeline: builder.query<TimelineResponse, GetTimelineParams | void>({
      query: (params) => ({
        url: '/timeline/me',
        params: params
          ? {
              ...params,
              types: params.types?.join(','),
            }
          : undefined,
      }),
      transformResponse: (response: { data: TimelineResponse }) => response.data,
      providesTags: ['Timeline'],
    }),

    // Get timeline statistics
    getMyTimelineStats: builder.query<TimelineStats, void>({
      query: () => '/timeline/me/stats',
      transformResponse: (response: { data: TimelineStats }) => response.data,
      providesTags: ['Timeline'],
    }),

    // Get public timeline by profile slug
    getPublicTimelineBySlug: builder.query<TimelineResponse, { slug: string } & GetTimelineParams>({
      query: ({ slug, ...params }) => ({
        url: `/timeline/slug/${slug}/public`,
        params: {
          ...params,
          types: params.types?.join(','),
        },
      }),
      transformResponse: (response: { data: TimelineResponse }) => response.data,
      providesTags: (_result, _error, { slug }) => [{ type: 'Timeline', id: slug }],
    }),
  }),
});

export const {
  useGetMyTimelineQuery,
  useGetMyTimelineStatsQuery,
  useGetPublicTimelineBySlugQuery,
} = timelineApi;
